/**
 * TaskActivityLog (§1.6 Build 2) — the per-task append-only activity feed.
 *
 * System events (done, reopened, dismissed, restored, attachment) and executor
 * notes render in ONE newest-first stream (NOTES-FIX-2 A-1 — matches the notes widget
 * convention), bounded + scrollable so a long log doesn't stretch the drawer.
 * Entries are never edited or deleted — this is the audit trail made visible.
 *
 * Events are fetched by the parent drawer (shared query key) and passed in; this
 * component owns only the note input + its mutation.
 */
import { useState } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { SAGE, WARM } from "@shared/brand";

export interface TaskEvent {
  id: string;
  eventType: string;
  recordRowId?: string | null;
  note: string | null;
  payload: { filename?: string; mimeType?: string; fileSize?: number; section?: string; action?: string; displayName?: string } | null;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
}


function eventLine(e: TaskEvent): { text: string; isNote: boolean } {
  switch (e.eventType) {
    case "done": return { text: "Marked done", isNote: false };
    case "reopen": return { text: "Reopened", isNote: false };
    case "dismissed": return { text: `Dismissed — doesn't apply${e.note ? ` · ${e.note}` : ""}`, isNote: false };
    case "restore": return { text: "Restored to the list", isNote: false };
    case "attachment": return { text: `Attached ${e.payload?.filename ?? "a file"}`, isNote: false };
    case "note": return { text: e.note ?? "", isNote: true };
    case "letter_generated": return { text: `Generated ${e.payload?.displayName ?? "a notification letter"}`, isNote: false };
    case "record_action": return {
      text: `${e.payload?.action === "done" ? "Checked off a record" : "Un-checked a record"}${e.note ? ` · ${e.note}` : ""}`,
      isNote: false,
    };
    default: return { text: e.eventType.replace(/_/g, " "), isNote: false };
  }
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function TaskActivityLog({
  clientId, taskKey, events, eventsKey,
}: {
  clientId: string;
  taskKey: string;
  events: TaskEvent[];
  eventsKey: unknown[];
}) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);

  const addNote = useMutation({
    mutationFn: async (note: string) =>
      (await apiRequest("POST", `/api/executor/${clientId}/tasks/${taskKey}/note`, { note })).json(),
    onSuccess: () => {
      setDraft("");
      qc.invalidateQueries({ queryKey: eventsKey });
      qc.invalidateQueries({ queryKey: [`/api/executor/${clientId}/tasks`] });
      // A drawer note is a task_events 'note' row, which the estate-wide notes widget
      // unions into its consolidated list (s124 Fix 5). Invalidate the widget's key too so
      // it refetches instead of serving its cached list (NOTES-FIX-2 A-0, s71 WIE class).
      qc.invalidateQueries({ queryKey: [`/api/executor/${clientId}/notes`] });
    },
  });

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): notes are append-only `task_events`, so a double-fire posts
  // the note twice into a permanent audit stream.
  const noteFlight = useSingleFlight();

  const submit = () => {
    const note = draft.trim();
    if (note) noteFlight.runQuiet(() => addNote.mutateAsync(note));
  };

  return (
    <div className="mt-4">
      {events.length > 0 && (
        <button type="button" onClick={() => setOpen((o) => !o)}
          className="text-[13px] font-medium mb-2" style={{ color: WARM.taupe }}
          data-testid={`activity-toggle-${taskKey}`}>
          {open ? "▾" : "▸"} Activity ({events.length})
        </button>
      )}
      {events.length > 0 && open && (
        // Newest-first (events arrive oldest-first from GET /events) + bounded height so
        // ~6 rows show and the rest scroll (NOTES-FIX-2 A-1). slice() to not mutate the prop.
        <ul className="flex flex-col gap-1.5 mb-3 overflow-y-auto" style={{ maxHeight: 160 }} data-testid={`activity-log-${taskKey}`}>
          {events.slice().reverse().map((e) => {
            const { text, isNote } = eventLine(e);
            return (
              <li key={e.id} className="text-[13px] leading-snug flex gap-2">
                <span className="shrink-0 tabular-nums" style={{ color: WARM.taupe, minWidth: 46 }}>{fmtDate(e.createdAt)}</span>
                <span style={{ color: isNote ? WARM.charcoal : WARM.taupe, paddingLeft: isNote ? 4 : 0 }}>{text}</span>
              </li>
            );
          })}
        </ul>
      )}
      <div className="flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(); }}
          rows={1}
          placeholder="Add a note…"
          className="flex-1 resize-none rounded-md px-3 py-1.5 text-[14px] outline-none"
          style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}`, color: WARM.charcoal }}
          data-testid={`note-input-${taskKey}`}
        />
        <button
          type="button"
          onClick={submit}
          disabled={!draft.trim() || addNote.isPending}
          className="rounded-md px-3 py-1.5 text-[13px] font-semibold text-white disabled:opacity-40"
          style={{ backgroundColor: SAGE[600] }}
          data-testid={`note-add-${taskKey}`}
        >
          Add
        </button>
      </div>
    </div>
  );
}
